// src/services/ai.js
import api from './api';

class AIService {
  /**
   * Get AI-powered solution suggestions for a problem
   * @param {string} problemId - The problem ID
   * @returns {Promise} - Promise with suggestions
   */
  async getSolutionSuggestions(problemId) {
    try {
      const data = await api.get(`/ai/suggestions/problem/${problemId}/`);
      return data;
    } catch (error) {
      console.error('Error getting solution suggestions:', error);
      return { suggestions: [], count: 0 };
    }
  }

  /**
   * Get AI-powered problem suggestions for a supplier
   * @param {string} organizationId - The supplier organization ID
   * @returns {Promise} - Promise with suggestions
   */
  async getProblemSuggestions(organizationId) {
    try {
      const data = await api.get(`/ai/suggestions/supplier/?organization_id=${organizationId}`);
      return data;
    } catch (error) {
      console.error('Error getting problem suggestions:', error);
      return { suggestions: [], count: 0 };
    }
  }

  /**
   * Get similar problems
   * @param {string} problemId - The problem ID
   * @returns {Promise} - Promise with similar problems
   */
  async getSimilarProblems(problemId) {
    try {
      const data = await api.get(`/ai/similar/problems/${problemId}/`);
      return data;
    } catch (error) {
      console.error('Error getting similar problems:', error);
      return { similar_problems: [], count: 0 };
    }
  }

  /**
   * Notify suppliers about a problem
   * @param {string} problemId - The problem ID
   * @returns {Promise} - Promise with notification result
   */
  async notifySuppliers(problemId) {
    try {
      const data = await api.post(`/ai/notify/suppliers/${problemId}/`);
      return data;
    } catch (error) {
      console.error('Error notifying suppliers:', error);
      return { message: 'Failed to notify suppliers', suggestions: [] };
    }
  }

  /**
   * Get AI-powered price prediction for a solution
   * @param {string} solutionId - The solution ID
   * @returns {Promise} - Promise with price prediction data
   */
  async getPricePrediction(solutionId) {
    try {
      const data = await api.get(`/ai/predict-price/${solutionId}/`);
      return data;
    } catch (error) {
      console.error('Error getting price prediction:', error);
      return { 
        predicted_price: null, 
        confidence: 0, 
        error: error.data?.message || 'Failed to get price prediction' 
      };
    }
  }

  /**
   * Generate AI-powered bid proposal for a problem
   * @param {string} problemId - The problem ID
   * @param {boolean} autoSubmit - Whether to auto-submit the bid (premium only)
   * @returns {Promise} - Promise with bid proposal data
   */
  async generateBidProposal(problemId, autoSubmit = false) {
    try {
      const data = await api.post(`/ai/generate-bid/${problemId}/`, { auto_submit: autoSubmit });
      return data;
    } catch (error) {
      console.error('Error generating bid proposal:', error);
      return { 
        error: error.data?.message || 'Failed to generate bid proposal',
        proposal: null 
      };
    }
  }

  /**
   * Get AI-powered challenge predictions for a supplier (Premium feature)
   * @param {string} organizationId - The supplier organization ID
   * @returns {Promise} - Promise with challenge predictions
   */
  async getChallengePredictions(organizationId) {
    try {
      const data = await api.get(`/ai/predict-challenges/?organization_id=${organizationId}`);
      return data;
    } catch (error) {
      console.error('Error getting challenge predictions:', error);
      return { 
        predictions: [], 
        count: 0,
        error: error.data?.message || 'Failed to get challenge predictions'
      };
    }
  }

  /**
   * Get AI-powered market insights (Premium feature)
   * @returns {Promise} - Promise with market insights
   */
  async getMarketInsights() {
    try {
      const data = await api.get(`/ai/market-insights/`);
      return data;
    } catch (error) {
      console.error('Error getting market insights:', error);
      return { 
        error: error.data?.message || 'Failed to get market insights',
        is_premium: false 
      };
    }
  }

  /**
   * Get AI-powered solution recommendations based on user preferences
   * @param {Object} filters - Filter criteria
   * @returns {Promise} - Promise with recommendations
   */
  async getPersonalizedRecommendations(filters = {}) {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const data = await api.get(`/ai/recommendations/?${queryParams}`);
      return data;
    } catch (error) {
      console.error('Error getting recommendations:', error);
      return { recommendations: [], count: 0 };
    }
  }

  /**
   * Get AI-powered market trends analysis
   * @param {string} industry - Optional industry filter
   * @returns {Promise} - Promise with trends data
   */
  async getMarketTrends(industry = null) {
    try {
      const url = industry ? `/ai/trends/?industry=${industry}` : '/ai/trends/';
      const data = await api.get(url);
      return data;
    } catch (error) {
      console.error('Error getting market trends:', error);
      return { trends: [], error: error.data?.message };
    }
  }

  /**
   * Analyze solution fit for a specific problem
   * @param {string} problemId - The problem ID
   * @param {string} solutionId - The solution ID
   * @returns {Promise} - Promise with fit analysis
   */
  async analyzeSolutionFit(problemId, solutionId) {
    try {
      const data = await api.get(`/ai/analyze-fit/?problem_id=${problemId}&solution_id=${solutionId}`);
      return data;
    } catch (error) {
      console.error('Error analyzing solution fit:', error);
      return { 
        score: 0, 
        analysis: 'Unable to analyze fit at this time',
        error: error.data?.message 
      };
    }
  }

  /**
   * Get AI-powered proposal improvement suggestions
   * @param {string} proposalText - The proposal text to improve
   * @returns {Promise} - Promise with improvement suggestions
   */
  async improveProposal(proposalText) {
    try {
      const data = await api.post('/ai/improve-proposal/', { proposal: proposalText });
      return data;
    } catch (error) {
      console.error('Error improving proposal:', error);
      return { 
        improved_proposal: proposalText,
        suggestions: [],
        error: error.data?.message 
      };
    }
  }
}

const aiService = new AIService();
export default aiService;